import { INotificationListener, IPlugin } from '@microsoft/1ds-core-js';
import { IXHROverride } from '@microsoft/1ds-post-js';
import { FilledTelemetryEvent } from '@microsoft/oteljs';

/**
 * Endpoint to send 1DS telemetry events to. Includes public and special environments endpoints.
 */
export enum OneDSEndpoint {
  /**
   * US Department of Defense endpoint (Pathfinder)
   */
  USGOV_DOD = 'https://pf.events.data.microsoft.com/OneCollector/1.0/',
  /**
   * US Department of Justice endpoint (Trailblazer)
   */
  USGOV_DOJ = 'https://tb.events.data.microsoft.com/OneCollector/1.0/',
  /**
   * Public endpoint
   */
  PUBLIC = 'https://browser.events.data.microsoft.com/OneCollector/1.0/',
  /**
   * Customer content
   */
  CUSTOMER_CONTENT = 'https://office-c.events.data.microsoft.com/OneCollector/1.0/',
  /**
   * EUDB endpoint
   */
  EUDB = 'https://eu-office.events.data.microsoft.com/OneCollector/1.0/'
}

/**
 * Properties for configuring a OneDS Helper
 */
export interface OneDSHelperProperties {
  /**
   * Enable sending customer content to a compliant pipeline
   */
  enableCustomerContent?: boolean;

  /**
   * Enable sending events with a data classification of 'Optional'
   */
  enableOptionalEvents?: boolean;
}

/**
 * Basic config properties for the OneDS sink. Only key-value pairs, no objects/functions
 */
export interface OneDSSinkConfig extends OneDSHelperProperties {
  /**
   * The endpoint URL. Should generally be OneDSEndpoint.*, but can also be specified as a custom URL string.
   */
  endpointUrl: OneDSEndpoint | string;

  /**
   * The frequency in milliseconds with which events are batched.
   */
  uploadFrequency?: number;

  /**
   * The number of events that can be kept in memory before the SDK starts to drop events. By default, this is 10,000.
   */
  eventsLimitInMem?: number;

  /**
   * Extension Config to be passed to the underlying SDK
   */
  extensionConfig?: {
    [key: string]: any;
  };
}

/**
 * Properties for configuring the 1DS sink
 */
export interface OneDSSinkProperties extends OneDSSinkConfig {
  /**
   * Adds a notification listener. The SDK calls methods on the listener when an appropriate notification
   * (ex: event sent/dropped) is raised.
   */
  notificationListener?: INotificationListener;

  /**
   * The HTTP override that should be used to send requests.
   * CAUTION: The SDK does not provide an indication of whether the request is made during shutdown, and latest
   * version of the modern browsers no longer allow synchronous requests during shutdown (and asynchronous ones
   * will be simple ignored). In the idea case the SDK would provide that information which the caller can then
   * use to call special fetch w/ keepalive or sendbeacon API, but since it doesn't you can only safely use
   * this override for unittesting purposes.
   */
  httpXHROverride?: IXHROverride;

  /**
   * Override for setTimeout. OneDS SDK uses setTimeout for scheduling the upload task and event notification callbacks.
   */
  setTimeoutOverride?: typeof setTimeout;

  /**
   * Override for clearTimeout. OneDS SDK uses setTimeout for rescheduling the upload task.
   */
  clearTimeoutOverride?: typeof clearTimeout;

  /**
   * Callback functions for SDK performance characteristics.
   */
  stats?: IStatsCallbacks;

  /**
   * Disables OTel's automatic tracking of SDKs processed/sent/dropped events.
   */
  disableStatsTracking?: boolean;

  /**
   * Plugins to be passed to the underlying SDK
   */
  plugins?: IPlugin[];
}

/**
 * An interface for callback functions for performance metrics
 */
export interface IStatsCallbacks {
  /**
   * Callback with how long the SDK takes to process (serialize + send) a batch of events into one request.
   */
  requestProcessingStats: TimeStatsCallback;

  /**
   * Callback with how many bytes the SDK puts out on the wire.
   */
  networkStats: NetworkStatsCallback;
}

export type TimeStatsCallback = (duration: number, numOfEvents: number) => void;
export type NetworkStatsCallback = (size: number) => void;

/**
 * Interface for telemetry preprocessors
 */
export interface Preprocessor {
  /**
   * The name of the preprocessor
   */
  readonly name: string;
  /**
   * Preprocessors process a telemetry event, and return true if the event can be sent.
   * They return false if an event cannot be sent, and they throw if there is an error.
   */
  processEvent: (telemetryEvent: FilledTelemetryEvent) => boolean;
}
